import { defineComponent, ref, onMounted, watch } from 'vue';
import Header from '@/components/HeaderDashboard.vue';
import Sidebar from '@/components/Sidebar.vue';
import { config as apiConfig } from '@/config';
import { hasPermission } from '@/store/userStore';
import { PERMISSIONS } from '@/constants/permissions';
import Swal from 'sweetalert2';
import { SearchIcon, PlusIcon, EditIcon, TrashIcon, XIcon, TagIcon, Loader2Icon, ChevronLeftIcon, ChevronRightIcon, BadgePercentIcon, BoxIcon, LinkIcon, SaveIcon, PowerIcon, HashIcon, PercentIcon, CalendarIcon, HistoryIcon, ClockIcon, ArrowRightIcon } from 'lucide-vue-next';
// Utility for toasts like in Retail
const Toast = Swal.mixin({
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
    didOpen: (toast) => {
        toast.addEventListener('mouseenter', Swal.stopTimer);
        toast.addEventListener('mouseleave', Swal.resumeTimer);
    }
});
export default defineComponent({
    name: 'ModuloPrecios',
    components: {
        Header, Sidebar,
        SearchIcon, PlusIcon, EditIcon, TrashIcon, XIcon,
        TagIcon, Loader2Icon, ChevronLeftIcon, ChevronRightIcon,
        BadgePercentIcon, BoxIcon, LinkIcon, SaveIcon, PowerIcon,
        HashIcon, PercentIcon, CalendarIcon, HistoryIcon, ClockIcon, ArrowRightIcon
    },
    setup() {
        const canEdit = ref(hasPermission(PERMISSIONS.PRECIOS.EDIT));
        const canManagePromos = ref(hasPermission(PERMISSIONS.PRECIOS.PROMO));
        const activeTab = ref('grilla');
        const getHeaders = () => ({
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
            'Content-Type': 'application/json'
        });
        // GRILLA & CONFIG
        const filtroPVD = ref('30');
        const getValPVD = (item) => {
            const val = filtroPVD.value;
            if (val === '7')
                return item.pvd7 || 0;
            if (val === '15')
                return item.pvd15 || 0;
            if (val === '30')
                return item.pvd30 || 0;
            if (val === '60')
                return item.pvd60 || 0;
            if (val === '90')
                return item.pvd90 || 0;
            return 0;
        };
        const isLoadingGrilla = ref(false);
        const isSavingConfig = ref(false);
        const config = ref({ porcentajeUtilidadMayor: 0 });
        const grilla = ref({ items: [], totalItems: 0, totalPaginas: 0 });
        const filtro = ref({ busqueda: '', pagina: 1, tamanoPagina: 50 });
        const loadConfig = async () => {
            try {
                const res = await fetch(`${apiConfig.API_URL}/api/Precios/configuracion`, { headers: getHeaders() });
                const data = await res.json();
                if (res.ok && data.datos)
                    config.value.porcentajeUtilidadMayor = data.datos.porcentajeUtilidadMayor;
            }
            catch (error) {
                console.error('Error config', error);
            }
        };
        const saveConfig = async () => {
            if (!canEdit.value)
                return;
            isSavingConfig.value = true;
            try {
                const res = await fetch(`${apiConfig.API_URL}/api/Precios/configuracion`, {
                    method: 'PUT',
                    headers: getHeaders(),
                    body: JSON.stringify({ porcentajeUtilidadMayor: config.value.porcentajeUtilidadMayor })
                });
                if (res.ok) {
                    Toast.fire({ icon: 'success', title: 'Configuración de % Mayor actualizada' });
                    loadGrilla();
                }
                else {
                    throw new Error();
                }
            }
            catch (error) {
                Swal.fire('Error', 'No se pudo guardar', 'error');
            }
            finally {
                isSavingConfig.value = false;
            }
        };
        const loadGrilla = async () => {
            isLoadingGrilla.value = true;
            try {
                const params = new URLSearchParams({
                    busqueda: filtro.value.busqueda,
                    pagina: filtro.value.pagina.toString(),
                    tamanoPagina: filtro.value.tamanoPagina.toString()
                });
                const res = await fetch(`${apiConfig.API_URL}/api/Precios/grilla?${params}`, { headers: getHeaders() });
                const data = await res.json();
                if (res.ok && data.datos) {
                    grilla.value = data.datos;
                    if (grilla.value.items == null)
                        grilla.value.items = []; // Evita undefined exception
                    config.value.porcentajeUtilidadMayor = data.datos.porcentajeUtilidadMayor;
                }
            }
            catch (error) {
                console.error('Error grilla', error);
            }
            finally {
                isLoadingGrilla.value = false;
            }
        };
        const updateUtilidad = async (item) => {
            if (!canEdit.value || !item.equivalenteId)
                return;
            try {
                const res = await fetch(`${apiConfig.API_URL}/api/Precios/equivalente/${item.equivalenteId}/utilidad`, {
                    method: 'PATCH',
                    headers: getHeaders(),
                    body: JSON.stringify({ porcentaje: item.porcentajeUtilidadDetal })
                });
                if (res.ok) {
                    const costo = item.costoPromedio > 0 ? item.costoPromedio : item.ultimoCosto;
                    item.precioDetal = costo * (1 + item.porcentajeUtilidadDetal / 100);
                    item.precioAliado = (item.precioDetal + item.precioMayor) / 2;
                    if (item.porcentajePromocion) {
                        item.precioDetalConPromocion = item.precioDetal * (1 - item.porcentajePromocion / 100);
                    }
                    Toast.fire({ icon: 'success', title: '% Detal actualizado' });
                }
                else {
                    throw new Error();
                }
            }
            catch (error) {
                Swal.fire('Error', 'No se pudo actualizar', 'error');
            }
        };
        const changePage = (page) => {
            if (page >= 1 && page <= grilla.value.totalPaginas) {
                filtro.value.pagina = page;
                loadGrilla();
            }
        };
        // PROMOS
        const promociones = ref([]);
        const isLoadingPromos = ref(false);
        const filtroPromo = ref({
            busqueda: '',
            pagina: 1,
            tamanoPagina: 10,
            soloActivas: false,
            productoId: null,
            equivalenteId: null,
            marcaValorId: null
        });
        const promosMeta = ref({
            totalItems: 0,
            totalPaginas: 0
        });
        const showPromoModal = ref(false);
        const isSavingPromo = ref(false);
        const applyType = ref('producto');
        const promoForm = ref({
            id: null,
            targetId: null,
            porcentajeDescuento: 10,
            fechaInicio: '',
            fechaFin: '',
            activo: true
        });
        // AUDITORÍA DE COSTOS
        const showHistoryModal = ref(false);
        const isLoadingHistory = ref(false);
        const historyData = ref([]);
        const historyProductName = ref('');
        const openHistory = async (productoId, nombre) => {
            historyProductName.value = nombre;
            showHistoryModal.value = true;
            isLoadingHistory.value = true;
            try {
                const res = await fetch(`${apiConfig.API_URL}/api/Precios/producto/${productoId}/historial-costos`, { headers: getHeaders() });
                const data = await res.json();
                if (res.ok && data.datos) {
                    historyData.value = data.datos;
                }
            }
            catch (error) {
                console.error('Error loading history', error);
            }
            finally {
                isLoadingHistory.value = false;
            }
        };
        const formatTime = (dateStr) => {
            if (!dateStr)
                return '';
            return new Date(dateStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        };
        const loadPromociones = async () => {
            isLoadingPromos.value = true;
            try {
                const params = new URLSearchParams();
                if (filtroPromo.value.busqueda)
                    params.append('busqueda', filtroPromo.value.busqueda);
                params.append('pagina', filtroPromo.value.pagina.toString());
                params.append('tamanoPagina', filtroPromo.value.tamanoPagina.toString());
                params.append('soloActivas', filtroPromo.value.soloActivas.toString());
                if (filtroPromo.value.productoId)
                    params.append('productoId', filtroPromo.value.productoId.toString());
                if (filtroPromo.value.equivalenteId)
                    params.append('equivalenteId', filtroPromo.value.equivalenteId.toString());
                if (filtroPromo.value.marcaValorId)
                    params.append('marcaValorId', filtroPromo.value.marcaValorId.toString());
                const res = await fetch(`${apiConfig.API_URL}/api/Precios/promociones?${params.toString()}`, { headers: getHeaders() });
                const data = await res.json();
                if (res.ok && data.datos) {
                    promociones.value = data.datos.items || [];
                    promosMeta.value.totalItems = data.datos.totalItems;
                    promosMeta.value.totalPaginas = data.datos.totalPaginas;
                }
            }
            catch (error) {
                console.error('Error promos', error);
            }
            finally {
                isLoadingPromos.value = false;
            }
        };
        const changePagePromo = (newPage) => {
            if (newPage >= 1 && newPage <= promosMeta.value.totalPaginas) {
                filtroPromo.value.pagina = newPage;
                loadPromociones();
            }
        };
        const formatForInput = (dateStr) => {
            if (!dateStr)
                return '';
            const d = new Date(dateStr);
            d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
            return d.toISOString().slice(0, 16);
        };
        // Autocomplete Logic
        const searchTargetQuery = ref('');
        const targetSeleccionado = ref(false);
        const showTargetDropdown = ref(false);
        const searchResults = ref([]);
        let debounceTimer = null;
        const onSearchTargetInput = async () => {
            if (!searchTargetQuery.value) {
                clearTarget();
                return;
            }
            targetSeleccionado.value = false;
            promoForm.value.targetId = null;
            showTargetDropdown.value = true;
            const query = searchTargetQuery.value;
            if (query.length < 2) {
                searchResults.value = [];
                return;
            }
            clearTimeout(debounceTimer);
            debounceTimer = setTimeout(async () => {
                try {
                    if (applyType.value === 'producto') {
                        const res = await fetch(`${apiConfig.API_URL}/api/Producto/pos/search?searchTerm=${query}`, { headers: getHeaders() });
                        if (res.ok) {
                            const data = await res.json();
                            searchResults.value = data.datos.map((p) => ({ id: p.id, nombre: `${p.nombre} (${p.codigo})` }));
                        }
                    }
                    else if (applyType.value === 'marca') {
                        const res = await fetch(`${apiConfig.API_URL}/api/ValoresDesplegables/search/marcas?searchTerm=${query}`, { headers: getHeaders() });
                        if (res.ok) {
                            const data = await res.json();
                            searchResults.value = data.data.map((m) => ({ id: m.valorID, nombre: m.valor }));
                        }
                    }
                    else {
                        const res = await fetch(`${apiConfig.API_URL}/api/Equivalentes?search=${query}&page=1`, { headers: getHeaders() });
                        if (res.ok) {
                            const data = await res.json();
                            const items = data.datos.items || data.datos || [];
                            searchResults.value = items.map((e) => ({
                                id: e.id || e.equivalenteId,
                                nombre: e.nombreRepuesto ? `${e.codigoEquivalente} - ${e.nombreRepuesto}` : e.codigoEquivalente
                            }));
                        }
                    }
                }
                catch (e) {
                    console.error(e);
                }
            }, 400);
        };
        const hideTargetDropdown = () => setTimeout(() => { showTargetDropdown.value = false; }, 200);
        const seleccionarTarget = (id, nombre) => {
            promoForm.value.targetId = id;
            searchTargetQuery.value = nombre;
            targetSeleccionado.value = true;
            showTargetDropdown.value = false;
        };
        const clearTarget = () => {
            targetSeleccionado.value = false;
            promoForm.value.targetId = null;
            searchTargetQuery.value = '';
            searchResults.value = [];
        };
        // Unified Filter Promo logic
        const filterPromoQuery = ref('');
        const filterPromoResults = ref([]);
        const showFilterPromoDropdown = ref(false);
        let filterDebounce = null;
        const onFilterPromoInput = () => {
            if (!filterPromoQuery.value) {
                resetPromoFilter();
                return;
            }
            showFilterPromoDropdown.value = true;
            const query = filterPromoQuery.value;
            if (query.length < 2) {
                filterPromoResults.value = [];
                return;
            }
            clearTimeout(filterDebounce);
            filterDebounce = setTimeout(async () => {
                try {
                    const [resPrd, resEqv, resMrc] = await Promise.all([
                        fetch(`${apiConfig.API_URL}/api/Producto/pos/search?searchTerm=${query}`, { headers: getHeaders() }),
                        fetch(`${apiConfig.API_URL}/api/Equivalentes?search=${query}&page=1`, { headers: getHeaders() }),
                        fetch(`${apiConfig.API_URL}/api/ValoresDesplegables/search/marcas?searchTerm=${query}`, { headers: getHeaders() })
                    ]);
                    let results = [];
                    if (resPrd.ok) {
                        const data = await resPrd.json();
                        results = [...results, ...data.datos.map((p) => ({
                                id: p.id,
                                nombre: `${p.nombre} (${p.codigo})`,
                                tipo: 'producto',
                                key: `p-${p.id}`
                            }))];
                    }
                    if (resEqv.ok) {
                        const data = await resEqv.json();
                        const items = data.datos.items || data.datos || [];
                        results = [...results, ...items.map((e) => ({
                                id: e.id || e.equivalenteId,
                                nombre: e.nombreRepuesto ? `${e.codigoEquivalente} - ${e.nombreRepuesto}` : e.codigoEquivalente,
                                tipo: 'equivalente',
                                key: `e-${e.id || e.equivalenteId}`
                            }))];
                    }
                    if (resMrc.ok) {
                        const data = await resMrc.json();
                        results = [...results, ...data.data.map((m) => ({
                                id: m.valorID,
                                nombre: m.valor,
                                tipo: 'marca',
                                key: `m-${m.valorID}`
                            }))];
                    }
                    filterPromoResults.value = results;
                }
                catch (e) {
                    console.error(e);
                }
            }, 400);
        };
        const selectFilterPromoTarget = (item) => {
            if (item.tipo === 'producto') {
                filtroPromo.value.productoId = item.id;
                filtroPromo.value.equivalenteId = null;
                filtroPromo.value.marcaValorId = null;
            }
            else if (item.tipo === 'marca') {
                filtroPromo.value.marcaValorId = item.id;
                filtroPromo.value.productoId = null;
                filtroPromo.value.equivalenteId = null;
            }
            else {
                filtroPromo.value.equivalenteId = item.id;
                filtroPromo.value.productoId = null;
                filtroPromo.value.marcaValorId = null;
            }
            filterPromoQuery.value = item.nombre;
            showFilterPromoDropdown.value = false;
            loadPromociones();
        };
        const resetPromoFilter = () => {
            filtroPromo.value.productoId = null;
            filtroPromo.value.equivalenteId = null;
            filtroPromo.value.marcaValorId = null;
            filterPromoQuery.value = '';
            filterPromoResults.value = [];
            loadPromociones();
        };
        watch(applyType, () => {
            if (!promoForm.value.id) {
                clearTarget();
            }
        });
        const openPromoModal = (promo = null) => {
            clearTarget();
            if (promo) {
                promoForm.value = {
                    id: promo.id,
                    targetId: promo.productoId || promo.equivalenteId || promo.marcaValorId,
                    porcentajeDescuento: promo.porcentajeDescuento,
                    fechaInicio: formatForInput(promo.fechaInicio),
                    fechaFin: formatForInput(promo.fechaFin),
                    activo: promo.activo
                };
                applyType.value = promo.productoId ? 'producto' : (promo.marcaValorId ? 'marca' : 'equivalente');
                targetSeleccionado.value = true;
                searchTargetQuery.value = applyType.value === 'producto' ? promo.productoNombre : (applyType.value === 'marca' ? promo.marcaValorNombre : promo.equivalenteCodigo);
            }
            else {
                const now = new Date();
                const tomorrow = new Date(now);
                tomorrow.setDate(tomorrow.getDate() + 1);
                applyType.value = 'producto';
                promoForm.value = {
                    id: null,
                    targetId: null,
                    porcentajeDescuento: 10,
                    fechaInicio: formatForInput(now.toISOString()),
                    fechaFin: formatForInput(tomorrow.toISOString()),
                    activo: true
                };
            }
            showPromoModal.value = true;
        };
        const savePromo = async () => {
            isSavingPromo.value = true;
            try {
                const payload = {
                    porcentajeDescuento: promoForm.value.porcentajeDescuento,
                    fechaInicio: new Date(promoForm.value.fechaInicio).toISOString(),
                    fechaFin: new Date(promoForm.value.fechaFin).toISOString(),
                    activo: promoForm.value.activo
                };
                if (!promoForm.value.id) {
                    if (applyType.value === 'producto')
                        payload.productoId = promoForm.value.targetId;
                    else if (applyType.value === 'marca')
                        payload.marcaValorId = promoForm.value.targetId;
                    else
                        payload.equivalenteId = promoForm.value.targetId;
                }
                else {
                    const existing = promociones.value.find(p => p.id === promoForm.value.id);
                    if (existing) {
                        payload.productoId = existing.productoId;
                        payload.equivalenteId = existing.equivalenteId;
                        payload.marcaValorId = existing.marcaValorId;
                    }
                }
                const url = promoForm.value.id
                    ? `${apiConfig.API_URL}/api/Precios/promociones/${promoForm.value.id}`
                    : `${apiConfig.API_URL}/api/Precios/promociones`;
                const method = promoForm.value.id ? 'PUT' : 'POST';
                const res = await fetch(url, {
                    method,
                    headers: getHeaders(),
                    body: JSON.stringify(payload)
                });
                if (res.ok) {
                    Toast.fire({ icon: 'success', title: promoForm.value.id ? 'Fechas/Estado actualizado' : 'Promoción creada' });
                    showPromoModal.value = false;
                    loadPromociones();
                    loadGrilla();
                }
                else {
                    const e = await res.json();
                    Swal.fire('Error', e.Mensaje || 'Error al guardar la promoción', 'error');
                }
            }
            catch (error) {
                Swal.fire('Error', 'No se pudo conectar con el servidor', 'error');
            }
            finally {
                isSavingPromo.value = false;
            }
        };
        const togglePromo = async (id) => {
            const result = await Swal.fire({
                title: '¿Cambiar estado?',
                text: "¿Estás seguro de que deseas activar/desactivar esta promoción?",
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: 'Sí, cambiar',
                cancelButtonText: 'Cancelar',
                confirmButtonColor: '#3b82f6',
                cancelButtonColor: '#94a3b8',
            });
            if (!result.isConfirmed)
                return;
            try {
                const res = await fetch(`${apiConfig.API_URL}/api/Precios/promociones/${id}/toggle`, {
                    method: 'PATCH',
                    headers: getHeaders()
                });
                if (res.ok) {
                    Toast.fire({ icon: 'success', title: 'Estado de la promoción actualizado' });
                    loadPromociones();
                    loadGrilla();
                }
                else {
                    Swal.fire('Error', 'No se pudo actualizar el estado', 'error');
                }
            }
            catch (e) {
                console.error(e);
            }
        };
        const deletePromo = async (id) => {
            const result = await Swal.fire({
                title: '¿Eliminar promoción?',
                text: "Esta acción no se puede deshacer",
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#d33',
                cancelButtonColor: '#3085d6',
                confirmButtonText: 'Sí, eliminar',
                cancelButtonText: 'Cancelar'
            });
            if (result.isConfirmed) {
                try {
                    const res = await fetch(`${apiConfig.API_URL}/api/Precios/promociones/${id}`, { method: 'DELETE', headers: getHeaders() });
                    if (res.ok) {
                        Toast.fire({ icon: 'success', title: 'Promoción eliminada' });
                        loadPromociones();
                    }
                }
                catch (error) {
                    Swal.fire('Error', 'Ocurrió un error al eliminar', 'error');
                }
            }
        };
        const formatNumber = (num) => Number(num).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        const formatDate = (dateStr) => new Date(dateStr).toLocaleString('es-ES', { year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' });
        onMounted(() => {
            loadConfig();
            loadGrilla();
            loadPromociones();
        });
        return {
            canEdit, canManagePromos, activeTab,
            isLoadingGrilla, isSavingConfig, config, grilla, filtro,
            saveConfig, loadGrilla, updateUtilidad, changePage,
            promociones, promosMeta, isLoadingPromos, filtroPromo, loadPromociones, changePagePromo,
            showPromoModal, isSavingPromo, applyType, promoForm,
            openPromoModal, savePromo, togglePromo, deletePromo,
            searchTargetQuery, targetSeleccionado, showTargetDropdown, searchResults,
            onSearchTargetInput, hideTargetDropdown, seleccionarTarget, clearTarget,
            formatNumber, formatDate,
            filtroPVD, getValPVD,
            filterPromoQuery, filterPromoResults, showFilterPromoDropdown,
            onFilterPromoInput, selectFilterPromoTarget, resetPromoFilter,
            showHistoryModal, isLoadingHistory, historyData, historyProductName, openHistory, formatTime
        };
    }
});
